/**
 * Foundry coverage (P0-4b deliverable 5): one row per whitelisted dnd5e 5.x
 * path of an actor export (the player-character paths and the NPC paths),
 * with its CharacterV1 target, its StatBlockV1 target, or null in both with
 * a reason. `fromFoundry` (2.G) follows it.
 *
 * Paths use the dnd5e 5.0–5.2 layout. `*` stands for one key (an ability, a
 * skill, an activity id); a trailing `.**` covers a whole subtree. Module ids
 * named below are public software names.
 */
import { isPrefixOf, leafPaths, patternMatches } from './foundry-paths.js';
export { generalizePath, isPrefixOf, leafPaths, patternMatches, tokens } from './foundry-paths.js';
const rows = [];
function row(path, character, statblock, reason) {
    rows.push(reason === undefined ? { path, character, statblock } : { path, character, statblock, reason });
}
function drop(path, reason) {
    row(path, null, null, reason);
}
const PC_ONLY = 'Player characters only';
const NPC_ONLY = 'NPCs only';
// ------------------------------------------------------------------ identity
row('name', 'name', 'name');
drop('type', 'Decides actor_sheet.kind (character or statblock), outside the sheet data');
// ----------------------------------------------------------------- abilities
row('system.abilities.*.value', 'abilities.*.score', 'abilities.*');
row('system.abilities.*.proficient', 'abilities.*.saveProficient', 'savingThrows');
row('system.abilities.*.max', 'abilities.*.max', null, PC_ONLY);
row('system.abilities.*.bonuses.check', 'abilities.*.bonuses.check', null, PC_ONLY);
row('system.abilities.*.bonuses.save', 'abilities.*.bonuses.save', null, PC_ONLY);
// ---------------------------------------------------------------- attributes
row('system.attributes.ac.calc', 'ac.calc', 'armorClass');
row('system.attributes.ac.flat', 'ac.flat', 'armorClass[].value');
row('system.attributes.ac.formula', 'ac.formula', 'armorClass[].source');
row('system.attributes.hp.value', 'hp.value', 'state.hitPoints');
row('system.attributes.hp.max', 'hp.max', 'hitPoints.average');
row('system.attributes.hp.formula', null, 'hitPoints.formula', NPC_ONLY);
row('system.attributes.hp.temp', 'hp.temp', 'state.tempHitPoints');
row('system.attributes.hp.tempmax', 'hp.tempMax', 'state.tempMaxHitPoints');
row('system.attributes.hp.bonuses.level', 'hp.bonuses.level', null, PC_ONLY);
row('system.attributes.hp.bonuses.overall', 'hp.bonuses.overall', null, PC_ONLY);
row('system.attributes.hd.spent', null, 'state.hitDiceSpent', NPC_ONLY);
row('system.attributes.init.ability', 'initiative.ability', null, PC_ONLY);
row('system.attributes.init.bonus', 'initiative.bonus', 'initiativeBonus');
for (const speed of ['walk', 'burrow', 'climb', 'fly', 'swim']) {
    row(`system.attributes.movement.${speed}`, `movement.${speed}`, `speed.${speed}`);
}
row('system.attributes.movement.units', 'movement.units', 'speed', 'A stat block gives speeds in feet');
row('system.attributes.movement.hover', 'movement.hover', 'speed.hover');
row('system.attributes.movement.bonus', 'movement.bonus', null, PC_ONLY);
row('system.attributes.movement.special', 'movement.special', 'speed.note');
row('system.attributes.movement.ignoredDifficultTerrain[]', 'movement.ignoredDifficultTerrain[]', null, PC_ONLY);
for (const sense of ['darkvision', 'blindsight', 'tremorsense', 'truesight']) {
    row(`system.attributes.senses.${sense}`, `senses.${sense}`, `senses.${sense}`);
}
row('system.attributes.senses.units', 'senses.units', 'senses', 'A stat block gives senses in feet');
row('system.attributes.senses.special', 'senses.special', 'senses.note');
row('system.attributes.spellcasting', 'spellcastingAbility', 'spellcasting[].ability');
row('system.attributes.spell.level', null, 'spellcasting[].casterLevel', NPC_ONLY);
row('system.attributes.death.success', 'death.success', null, PC_ONLY);
row('system.attributes.death.failure', 'death.failure', null, PC_ONLY);
row('system.attributes.exhaustion', 'exhaustion', null, PC_ONLY);
row('system.attributes.inspiration', 'inspiration', null, PC_ONLY);
row('system.attributes.concentration.limit', 'concentration.limit', null, PC_ONLY);
row('system.attributes.attunement.max', 'attunement.max', null, PC_ONLY);
drop('system.attributes.price.value', "An NPC's hire price; not part of a 2014 stat block");
drop('system.attributes.price.denomination', "An NPC's hire price; not part of a 2014 stat block");
// ------------------------------------------------------------------- details
row('system.details.race', 'details.race', null, PC_ONLY);
row('system.details.background', 'details.background', null, PC_ONLY);
row('system.details.originalClass', 'details.originalClass', null, PC_ONLY);
row('system.details.alignment', 'details.alignment', 'alignment');
row('system.details.xp.value', 'details.xp', 'challenge.xp');
for (const field of ['trait', 'ideal', 'bond', 'flaw']) {
    row(`system.details.${field}`, `details.${field}`, null, PC_ONLY);
}
for (const field of [
    'appearance',
    'age',
    'eyes',
    'faith',
    'gender',
    'hair',
    'height',
    'skin',
    'weight',
]) {
    row(`system.details.${field}`, `details.appearance.${field}`, null, PC_ONLY);
}
row('system.details.biography.value', 'details.biography.value', null, 'A stat block keeps no biography');
row('system.details.biography.public', 'details.biography.public', null, 'A stat block keeps no biography');
row('system.details.cr', null, 'challenge.rating', NPC_ONLY);
row('system.details.type.value', null, 'creatureType.value', NPC_ONLY);
row('system.details.type.subtype', null, 'creatureType.subtype', NPC_ONLY);
row('system.details.type.swarm', null, 'creatureType.swarmSize', NPC_ONLY);
row('system.details.type.custom', null, 'creatureType.custom', NPC_ONLY);
row('system.details.habitat', null, 'habitat', NPC_ONLY);
row('system.details.habitat.value[].type', null, 'habitat[].type', NPC_ONLY);
row('system.details.habitat.value[].subtype', null, 'habitat[].subtype', NPC_ONLY);
row('system.details.habitat.custom', null, 'habitat', NPC_ONLY);
row('system.details.treasure', null, 'treasure', NPC_ONLY);
row('system.details.treasure.value[]', null, 'treasure[]', NPC_ONLY);
// CS r6b (2.G s4's request): P0-7's Acolyte holds the array form `treasure: []`, dnd5e 5.x `{value: []}`.
row('system.details.treasure[]', null, 'treasure[]', NPC_ONLY);
// ----------------------------------------------------------------- resources
for (const key of ['primary', 'secondary', 'tertiary']) {
    for (const field of ['value', 'max', 'sr', 'lr', 'label']) {
        row(`system.resources.${key}.${field}`, `resources[].${field}`, null, PC_ONLY);
    }
}
row('system.resources.legact.max', null, 'legendary.perRound', NPC_ONLY);
row('system.resources.legact.spent', null, 'state.legendaryActionsSpent', NPC_ONLY);
row('system.resources.legres.max', null, 'legendaryResistance', NPC_ONLY);
row('system.resources.legres.spent', null, 'state.legendaryResistancesSpent', NPC_ONLY);
row('system.resources.lair.value', null, 'lair', NPC_ONLY);
row('system.resources.lair.initiative', null, 'lair.initiative', NPC_ONLY);
row('system.resources.lair.inside', null, 'lair.inside', NPC_ONLY);
// ------------------------------------------------------------ skills, tools
row('system.skills.*.value', 'skills[].value', 'skills');
row('system.skills.*.ability', 'skills[].ability', null, PC_ONLY);
row('system.skills.*.bonuses.check', 'skills[].bonuses.check', null, PC_ONLY);
row('system.skills.*.bonuses.passive', 'skills[].bonuses.passive', null, PC_ONLY);
row('system.skills.*.roll.mode', 'skills[].rollMode', null, PC_ONLY);
row('system.tools.*.value', 'tools[].value', null, PC_ONLY);
row('system.tools.*.ability', 'tools[].ability', null, PC_ONLY);
row('system.tools.*.bonuses.check', 'tools[].bonuses.check', null, PC_ONLY);
// -------------------------------------------------------------------- spells
for (let level = 1; level <= 9; level++) {
    row(`system.spells.spell${level}`, 'spellSlots', 'spellcasting[].byLevel');
    row(`system.spells.spell${level}.value`, 'spellSlots[].value', null, PC_ONLY);
    row(`system.spells.spell${level}.override`, 'spellSlots[].override', 'spellcasting[].byLevel[].slots');
}
row('system.spells.pact', 'pactSlots', null, PC_ONLY);
row('system.spells.pact.value', 'pactSlots.value', null, PC_ONLY);
row('system.spells.pact.override', 'pactSlots.override', null, PC_ONLY);
drop('system.spells.spell0.**', 'A stray key that dnd5e ignores (amendments 2.2 section 3)');
// ------------------------------------------------------------------- bonuses
for (const kind of ['mwak', 'rwak', 'msak', 'rsak']) {
    row(`system.bonuses.${kind}.attack`, `bonuses.${kind}.attack`, null, PC_ONLY);
    row(`system.bonuses.${kind}.damage`, `bonuses.${kind}.damage`, null, PC_ONLY);
}
for (const field of ['check', 'save', 'skill']) {
    row(`system.bonuses.abilities.${field}`, `bonuses.abilities.${field}`, null, PC_ONLY);
}
row('system.bonuses.spell.dc', 'bonuses.spell.dc', null, PC_ONLY);
// ------------------------------------------------------------------ currency
row('system.currency.*', 'currency', null, 'Every key is kept; a stat block has no purse');
// -------------------------------------------------------------------- traits
row('system.traits.size', 'traits.size', 'size');
const DAMAGE_TRAITS = [
    ['di', 'damageImmunities'],
    ['dr', 'damageResistances'],
    ['dv', 'damageVulnerabilities'],
];
for (const [key, block] of DAMAGE_TRAITS) {
    row(`system.traits.${key}`, `traits.${key}`, block);
    row(`system.traits.${key}.value[]`, `traits.${key}.values[]`, `${block}[].types`);
    row(`system.traits.${key}.bypasses[]`, `traits.${key}.bypasses[]`, `${block}[].bypasses`);
    row(`system.traits.${key}.custom`, `traits.${key}.custom`, `${block}[].qualifier`);
}
row('system.traits.ci', 'traits.ci', 'conditionImmunities');
row('system.traits.ci.value[]', 'traits.ci.values[]', 'conditionImmunities[]');
row('system.traits.ci.custom', 'traits.ci.custom', null, 'A stat block lists only the 2014 conditions');
row('system.traits.dm.amount.*', 'traits.dm.amounts[].formula', null, PC_ONLY);
row('system.traits.dm.bypasses[]', 'traits.dm.bypasses[]', null, PC_ONLY);
row('system.traits.languages', 'traits.languages', 'languages');
row('system.traits.languages.value[]', 'traits.languages.values[]', 'languages.values[]');
row('system.traits.languages.custom', 'traits.languages.custom[]', 'languages.note');
row('system.traits.languages.communication.*.value', 'traits.languages.communication[].value', 'languages.telepathy');
row('system.traits.languages.communication.*.units', 'traits.languages.communication[].units', null, PC_ONLY);
row('system.traits.weaponProf.value[]', 'traits.weaponProf.values[]', null, PC_ONLY);
row('system.traits.weaponProf.custom', 'traits.weaponProf.custom', null, PC_ONLY);
drop('system.traits.weaponProf.mastery.**', 'Weapon mastery is a 2024 rule (appendix A section 3)');
row('system.traits.armorProf.value[]', 'traits.armorProf.values[]', null, PC_ONLY);
row('system.traits.armorProf.custom', 'traits.armorProf.custom', null, PC_ONLY);
drop('system.traits.important', "Foundry's display flag for an important NPC; not part of a stat block");
// ------------------------------------------------------------ favorites etc.
row('system.favorites[].type', 'favorites[].type', null, PC_ONLY);
row('system.favorites[].id', 'favorites[].id', null, PC_ONLY);
row('system.favorites[].sort', 'favorites[].sort', null, PC_ONLY);
row('system.source.rules', 'rules', 'rules');
for (const field of ['custom', 'book', 'page', 'license', 'revision']) {
    drop(`system.source.${field}`, 'Citation metadata; kept in the raw asset');
}
// --------------------------------------------------------------------- flags
/** dnd5e's special traits (`flags.dnd5e`), which change derived values. */
export const SPECIAL_TRAIT_FLAGS = [
    'diamondSoul',
    'elvenAccuracy',
    'enhancedDualWielding',
    'halflingLucky',
    'initiativeAdv',
    'initiativeAlert',
    'initiativeHalfProf',
    'jackOfAllTrades',
    'meleeCriticalDamageDice',
    'observantFeat',
    'powerfulBuild',
    'reliableTalent',
    'remarkableAthlete',
    'savageAttacks',
    'spellCriticalThreshold',
    'tavernBrawlerFeat',
    'weaponCriticalThreshold',
    'wildMagic',
];
for (const flag of SPECIAL_TRAIT_FLAGS) {
    row(`flags.dnd5e.${flag}`, 'specialTraits[].value', null, PC_ONLY);
}
drop('flags.ddbimporter.dndbeyond.characterId', 'Stored in actor_sheet.external_ids.ddb, outside the sheet data (amendments 2.2 section 7)');
drop('flags.ddbimporter.dndbeyond.url', 'Stored in actor_sheet.external_ids.ddb, outside the sheet data (amendments 2.2 section 7)');
drop('flags.tidy5e-sheet.maxPreparedSpells', 'A cross-check for the prepared-spell warning only');
// --------------------------------------------------------------------- items
row('items[]', 'items', 'actions');
row('items[]._id', 'items[].foundryId', null, 'A stat block feature keeps its Foundry id in foundry_map');
row('items[].name', 'items[].name', 'actions[].name');
row('items[].type', 'items[].type', 'actions', 'The type decides which list a feature joins');
row('items[].sort', 'items[].sort', 'actions', 'Features keep (sort, index) order');
drop('items[].img', 'Images are uploaded separately (appendix A section 7)');
row('items[].system.description.value', 'items[].description', 'actions[].text');
drop('items[].system.description.chat', 'The chat-card text, empty in 5.x exports');
drop('items[].system.unidentified.description', 'Shown only in Foundry before identification');
row('items[].system.identifier', 'items[].identifier', null, PC_ONLY);
row('items[].system.source.rules', 'items[].rules', 'rules');
for (const field of ['custom', 'book', 'page', 'license', 'revision']) {
    drop(`items[].system.source.${field}`, 'Citation metadata; kept in the raw asset');
}
row('items[].system.uses.max', 'items[].uses.max', 'actions[].usage');
row('items[].system.uses.spent', 'items[].uses.spent', null, PC_ONLY);
row('items[].system.uses.recovery[].period', 'items[].uses.recovery[].period', 'actions[].usage');
row('items[].system.uses.recovery[].type', 'items[].uses.recovery[].type', null, PC_ONLY);
row('items[].system.uses.recovery[].formula', 'items[].uses.recovery[].formula', null, PC_ONLY);
row('items[].system.uses.autoDestroy', 'items[].consumable.autoDestroy', null, PC_ONLY);
row('items[].system.quantity', 'items[].quantity', null, PC_ONLY);
row('items[].system.weight.value', 'items[].weight.value', null, PC_ONLY);
row('items[].system.weight.units', 'items[].weight.units', null, PC_ONLY);
row('items[].system.price.value', 'items[].price.value', null, PC_ONLY);
row('items[].system.price.denomination', 'items[].price.denomination', null, PC_ONLY);
row('items[].system.rarity', 'items[].rarity', null, PC_ONLY);
row('items[].system.equipped', 'items[].equipped', null, PC_ONLY);
row('items[].system.attunement', 'items[].attunement', null, PC_ONLY);
row('items[].system.attuned', 'items[].attuned', null, PC_ONLY);
row('items[].system.container', 'items[].containerId', null, PC_ONLY);
row('items[].system.properties[]', 'items[].weapon.properties[]', null, 'The item type decides which block');
row('items[].system.type.value', 'items[].weapon.type.value', null, 'The item type decides which block');
row('items[].system.type.baseItem', 'items[].weapon.type.detail', null, 'The item type decides which block');
row('items[].system.type.subtype', 'items[].feat.type.detail', null, 'The item type decides which block');
/** dnd5e 5.x's damage part fields. */
const DAMAGE_PART_FIELDS = [
    ['number', 'number'],
    ['denomination', 'denomination'],
    ['bonus', 'bonus'],
    ['types[]', 'types[]'],
    ['custom.enabled', 'custom.enabled'],
    ['custom.formula', 'custom.formula'],
    ['scaling.mode', 'scaling.mode'],
    ['scaling.number', 'scaling.number'],
    ['scaling.formula', 'scaling.formula'],
];
for (const part of ['base', 'versatile']) {
    for (const [from, to] of DAMAGE_PART_FIELDS) {
        row(`items[].system.damage.${part}.${from}`, `items[].weapon.damage.${part}.${to}`, 'actions[].attack.damage');
    }
}
row('items[].system.range.value', 'items[].weapon.range.value', 'actions[].attack.rangeNormal');
row('items[].system.range.long', 'items[].weapon.range.long', 'actions[].attack.rangeLong');
row('items[].system.range.units', 'items[].weapon.range.units', null, 'A stat block gives ranges in feet');
row('items[].system.range.reach', 'items[].weapon.range.reach', 'actions[].attack.reach');
row('items[].system.range.special', 'items[].spell.range.special', null, PC_ONLY);
row('items[].system.proficient', 'items[].weapon.proficient', null, 'NPC weapons are always proficient');
row('items[].system.magicalBonus', 'items[].weapon.magicalBonus', null, PC_ONLY);
row('items[].system.armor.value', 'items[].equipment.armor.value', 'armorClass[].value');
row('items[].system.armor.dex', 'items[].equipment.armor.dex', null, PC_ONLY);
row('items[].system.armor.magicalBonus', 'items[].equipment.armor.magicalBonus', null, PC_ONLY);
row('items[].system.strength', 'items[].equipment.strength', null, PC_ONLY);
row('items[].system.ability', 'items[].tool.ability', null, 'For a spell, items[].spell.ability');
row('items[].system.capacity.weight.value', 'items[].containerStats.capacity.weight', null, PC_ONLY);
row('items[].system.capacity.weight.units', 'items[].containerStats.capacity.units', null, PC_ONLY);
// Spell items.
row('items[].system.level', 'items[].spell.level', 'spellcasting[].byLevel[].level');
row('items[].system.school', 'items[].spell.school', null, PC_ONLY);
row('items[].system.materials.value', 'items[].spell.materials.value', null, PC_ONLY);
row('items[].system.materials.consumed', 'items[].spell.materials.consumed', null, PC_ONLY);
row('items[].system.materials.cost', 'items[].spell.materials.cost', null, PC_ONLY);
row('items[].system.materials.supply', 'items[].spell.materials.supply', null, PC_ONLY);
row('items[].system.activation.type', 'items[].spell.activation.type', null, PC_ONLY);
row('items[].system.activation.value', 'items[].spell.activation.value', null, PC_ONLY);
row('items[].system.activation.condition', 'items[].spell.activation.condition', null, PC_ONLY);
row('items[].system.duration.value', 'items[].spell.duration.value', null, PC_ONLY);
row('items[].system.duration.units', 'items[].spell.duration.units', null, PC_ONLY);
drop('items[].system.target.**', 'Targeting is kept per activity, where dnd5e 5.x applies it');
row('items[].system.method', 'items[].spell.method', 'spellcasting[].kind');
row('items[].system.prepared', 'items[].spell.prepared', null, PC_ONLY);
row('items[].system.sourceClass', 'items[].spell.sourceClass', null, PC_ONLY);
// CS r6b (DECISIONS 770.14): dnd5e 5.0's spell form, read by 2.G's mappers (`MAPPER_EXTRA_ROWS`).
row('items[].system.preparation.mode', 'items[].spell.method', null, PC_ONLY);
row('items[].system.preparation.prepared', 'items[].spell.prepared', null, PC_ONLY);
// Feats.
row('items[].system.requirements', 'items[].feat.requirements', null, PC_ONLY);
// Classes and subclasses.
row('items[].system.levels', 'classes[].levels', null, PC_ONLY);
row('items[].system.hd.denomination', 'classes[].hitDie', null, PC_ONLY);
row('items[].system.hd.spent', 'classes[].hitDiceSpent', null, PC_ONLY);
row('items[].system.hd.additional', 'classes[].hitDiceAdditional', null, PC_ONLY);
row('items[].system.spellcasting.progression', 'classes[].spellcasting.progression', null, PC_ONLY);
row('items[].system.spellcasting.ability', 'classes[].spellcasting.ability', null, PC_ONLY);
row('items[].system.classIdentifier', 'classes[].subclass', null, PC_ONLY);
row('items[].system.advancement[]', 'classes[].hitPointAdvancement', null, PC_ONLY);
row('items[].system.advancement[].type', 'classes[].hitPointAdvancement', null, 'Only HitPoints advancements are kept');
row('items[].system.advancement[].value.*', 'classes[].hitPointAdvancement[].value', null, PC_ONLY);
// ---------------------------------------------------------------- activities
const A = 'items[].system.activities.*';
const T = 'items[].activities[]';
row(`${A}`, T, 'actions[].attack');
row(`${A}._id`, `${T}.foundryId`, null, PC_ONLY);
row(`${A}.type`, `${T}.type`, 'actions[].attack');
row(`${A}.name`, `${T}.name`, null, PC_ONLY);
row(`${A}.sort`, `${T}.sort`, null, PC_ONLY);
drop(`${A}.img`, 'Images are uploaded separately (appendix A section 7)');
for (const field of ['type', 'value', 'condition', 'override']) {
    row(`${A}.activation.${field}`, `${T}.activation.${field}`, null, PC_ONLY);
}
for (const field of ['type', 'target', 'value', 'scaling.mode', 'scaling.formula']) {
    row(`${A}.consumption.targets[].${field}`, `${T}.consumption.targets[].${field}`, null, PC_ONLY);
}
row(`${A}.consumption.scaling.allowed`, `${T}.consumption.scaling.allowed`, null, PC_ONLY);
row(`${A}.consumption.scaling.max`, `${T}.consumption.scaling.max`, null, PC_ONLY);
row(`${A}.consumption.spellSlot`, `${T}.consumption.spellSlot`, null, PC_ONLY);
for (const field of ['value', 'units', 'special', 'concentration', 'override']) {
    row(`${A}.duration.${field}`, `${T}.duration.${field}`, null, PC_ONLY);
}
for (const field of ['value', 'units', 'special', 'override']) {
    row(`${A}.range.${field}`, `${T}.range.${field}`, null, PC_ONLY);
}
for (const field of ['count', 'type', 'choice', 'special']) {
    row(`${A}.target.affects.${field}`, `${T}.target.affects.${field}`, 'actions[].attack.target');
}
for (const field of ['count', 'contiguous', 'type', 'size', 'width', 'height', 'units']) {
    row(`${A}.target.template.${field}`, `${T}.target.template.${field}`, null, PC_ONLY);
}
row(`${A}.target.prompt`, `${T}.target.prompt`, null, PC_ONLY);
row(`${A}.target.override`, `${T}.target.override`, null, PC_ONLY);
row(`${A}.uses.spent`, `${T}.uses.spent`, null, PC_ONLY);
row(`${A}.uses.max`, `${T}.uses.max`, null, PC_ONLY);
for (const field of ['period', 'type', 'formula']) {
    row(`${A}.uses.recovery[].${field}`, `${T}.uses.recovery[].${field}`, null, PC_ONLY);
}
row(`${A}.effects[]._id`, `${T}.effects[].id`, null, PC_ONLY);
row(`${A}.attack.ability`, `${T}.attack.ability`, null, PC_ONLY);
row(`${A}.attack.bonus`, `${T}.attack.bonus`, 'actions[].attack.toHit');
row(`${A}.attack.flat`, `${T}.attack.flat`, null, PC_ONLY);
row(`${A}.attack.critical.threshold`, `${T}.attack.criticalThreshold`, null, PC_ONLY);
row(`${A}.attack.type.value`, `${T}.attack.type.value`, 'actions[].attack.range');
row(`${A}.attack.type.classification`, `${T}.attack.type.classification`, 'actions[].attack.kind');
row(`${A}.damage.includeBase`, `${T}.damage.includeBase`, null, PC_ONLY);
row(`${A}.damage.onSave`, `${T}.damage.onSave`, null, PC_ONLY);
for (const [from, to] of DAMAGE_PART_FIELDS) {
    row(`${A}.damage.parts[].${from}`, `${T}.damage.parts[].${to}`, 'actions[].attack.damage');
    row(`${A}.healing.${from}`, `${T}.healing.${to}`, null, PC_ONLY);
}
row(`${A}.save.ability[]`, `${T}.save.ability[]`, null, PC_ONLY);
row(`${A}.save.dc.calculation`, `${T}.save.dc.calculation`, null, PC_ONLY);
row(`${A}.save.dc.formula`, `${T}.save.dc.formula`, null, PC_ONLY);
row(`${A}.check.ability`, `${T}.check.ability`, null, PC_ONLY);
row(`${A}.check.associated[]`, `${T}.check.associated[]`, null, PC_ONLY);
row(`${A}.check.dc.calculation`, `${T}.check.dc.calculation`, null, PC_ONLY);
row(`${A}.check.dc.formula`, `${T}.check.dc.formula`, null, PC_ONLY);
for (const field of ['formula', 'name', 'prompt', 'visible']) {
    row(`${A}.roll.${field}`, `${T}.roll.${field}`, null, PC_ONLY);
}
// The midi-qol keys dnd5e does not define, dropped from activities (appendix A section 5).
export const MIDI_ACTIVITY_KEYS = [
    'midiProperties',
    'macroData',
    'isOverTimeFlag',
    'overTimeProperties',
    'otherActivityId',
    'otherActivityUuid',
    'otherActivityAsParentType',
    'useConditionText',
    'useConditionReason',
    'effectConditionText',
    'ignoreTraits',
    'attackRollPerTarget',
    'fumbleThreshold',
    'friendlySave',
    'flags',
];
for (const key of MIDI_ACTIVITY_KEYS) {
    drop(`${A}.${key}.**`, 'A midi-qol key, not part of dnd5e; dropped (appendix A section 5)');
    drop(`${A}.${key}`, 'A midi-qol key, not part of dnd5e; dropped (appendix A section 5)');
}
// ------------------------------------------- dnd5e keys the sheet does not keep
const ROLL = 'Roll limits and modes: Foundry roll settings, not kept in the draft sheet';
for (const kind of ['check', 'save']) {
    for (const field of ['min', 'max', 'mode']) {
        drop(`system.abilities.*.${kind}.roll.${field}`, ROLL);
    }
}
for (const field of ['min', 'max'])
    drop(`system.skills.*.roll.${field}`, ROLL);
for (const field of ['min', 'max', 'mode']) {
    drop(`system.tools.*.roll.${field}`, ROLL);
    drop(`system.attributes.init.roll.${field}`, ROLL);
    drop(`system.attributes.death.roll.${field}`, ROLL);
    drop(`system.attributes.concentration.roll.${field}`, ROLL);
}
drop('system.attributes.death.bonuses.save', 'A death-save bonus formula; not kept in the draft sheet');
drop('system.attributes.concentration.ability', 'The concentration-save ability; not kept in the draft sheet');
drop('system.attributes.concentration.bonuses.save', 'A concentration-save bonus formula; not kept in the draft sheet');
drop('system.attributes.loyalty', 'A 2024 hireling field');
const ACTIVITY_DROP = 'An activity option used only inside Foundry';
drop(`${A}.ammunition`, ACTIVITY_DROP);
drop(`${A}.attackMode`, 'The attack mode is chosen when the attack is rolled in Foundry');
drop(`${A}.damage.critical.allow`, ACTIVITY_DROP);
drop(`${A}.damage.critical.bonus`, ACTIVITY_DROP);
drop(`${A}.description.chatFlavor`, 'Chat-card flavor text');
drop(`${A}.effects[].level.**`, ACTIVITY_DROP);
drop(`${A}.effects[].onSave`, ACTIVITY_DROP);
drop(`${A}.visibility.**`, 'Visibility rules apply only inside Foundry');
const ADVANCEMENT = 'Only the HitPoints advancement values are kept (appendix A section 3)';
drop('items[].system.advancement[]._id', ADVANCEMENT);
drop('items[].system.advancement[].configuration.**', ADVANCEMENT);
drop('items[].system.advancement[].level', ADVANCEMENT);
drop('items[].system.advancement[].title', ADVANCEMENT);
drop('items[].system.advancement[].value.chosen[]', ADVANCEMENT);
drop('items[].system.ammunition.**', 'Ammunition settings used only inside Foundry');
drop('items[].system.bonus', "A tool's check bonus formula; not kept in the draft sheet");
drop('items[].system.capacity.volume.**', 'Container volume, unused by dnd5e 5.x sheets');
drop('items[].system.chatFlavor', 'Chat-card flavor text');
drop('items[].system.cover', 'The cover an item grants; not kept in the draft sheet');
drop('items[].system.crew.**', 'Vehicle crew; not a character field');
drop('items[].system.crewed', 'Vehicle crew; not a character field');
row('items[].system.currency.*', 'items[].containerStats.currency', null, PC_ONLY);
drop('items[].system.damage.replace', 'A dnd5e 5.x damage option used only inside Foundry');
drop('items[].system.enchant.**', 'Enchantment bookkeeping used only inside Foundry');
drop('items[].system.hp.**', "An item's own hit points as an object; not kept on the sheet");
drop('items[].system.identified', "Foundry's identification toggle; the sheet shows the item as the GM owns it");
drop('items[].system.mastery', 'Weapon mastery is a 2024 rule');
drop('items[].system.prerequisites.**', 'Feat prerequisites; not kept in the draft sheet');
drop('items[].system.primaryAbility.**', "A class's primary ability (a 2024 rule)");
drop('items[].system.speed.**', 'An item-granted speed (vehicles); not a character field');
drop('items[].system.spellcasting.preparation.**', 'The prepared-spell formula; 2.G computes the 2014 maximum');
drop('items[].system.startingEquipment[].**', 'Starting-equipment choices; not kept on the sheet');
drop('items[].system.startingEquipment[]', 'Starting-equipment choices; not kept on the sheet');
// ------------------------------------------------------- item module flags
// CS r6a (the freeze; DECISIONS 216 ruling 6): the D&D Beyond item id is the sheet's `ddbId`, a
// secondary match key for a refresh; the importer's other two ids stay out of the sheet.
row('items[].flags.ddbimporter.id', 'items[].ddbId', null, 'A stat block feature keeps its ids in foundry_map');
for (const key of ['definitionId', 'entityTypeId']) {
    drop(`items[].flags.ddbimporter.${key}`, 'A secondary item-match key for re-import, read by fromFoundry; module flags are not part of the sheet');
}
drop('items[].flags.mmm.lingeringInjury', 'Read by fromFoundry as an "injury" tag hint; not part of the sheet');
drop('items[].flags.spell-class-filter-for-5e.parentClass', "A hint for a spell's class, read by fromFoundry; not part of the sheet");
drop('items[].flags.custom-character-sheet-sections.sectionName', 'An optional grouping hint; module flags are not part of the sheet');
// ---------------------------------------------------------------- effects
// CS r6a (the freeze; DECISIONS 216 ruling 1): an effect list maps to CharacterV1's `effects`,
// each entry in 2.G deliverable 6's shape: `_id` to `effectId`, `name`, `transfer`, `disabled`,
// and `changes[]` (`key`, `mode`, and `value` for dnd5e's own keys only); an item's effects carry
// that item's `_id` as `itemFoundryId`, the actor's own null. The list is the row, so a mapper
// that keeps the list beside the sheet (2.G s1 to s3) and one that fills it (s4) both satisfy it;
// the rest of an effect (duration, icon, origin, statuses, its own flags) stays in the raw asset.
row('effects[]', 'effects', null, 'Active effects on the actor itself (itemFoundryId null)');
row('items[].effects[]', 'effects', null, "An item's active effects (itemFoundryId its _id)");
const EFFECT_REST = 'Mapped into CharacterV1 effects through the list row (_id, name, transfer, disabled, changes); the rest of an effect stays in the raw asset';
drop('effects[].**', EFFECT_REST);
drop('items[].effects[].**', EFFECT_REST);
export const FOUNDRY_COVERAGE = rows;
/**
 * The subtrees of an actor export that fromFoundry reads. Every leaf under
 * one of them matches a coverage row or an ignored prefix.
 */
export const FOUNDRY_WHITELISTED_PREFIXES = [
    'name',
    'type',
    'system',
    'effects[]',
    'flags.dnd5e',
    'flags.ddbimporter.dndbeyond.characterId',
    'flags.ddbimporter.dndbeyond.url',
    'flags.tidy5e-sheet.maxPreparedSpells',
    'items[]._id',
    'items[].name',
    'items[].type',
    'items[].img',
    'items[].sort',
    'items[].system',
    'items[].effects[]',
    'items[].flags.ddbimporter.id',
    'items[].flags.ddbimporter.definitionId',
    'items[].flags.ddbimporter.entityTypeId',
    'items[].flags.mmm.lingeringInjury',
    'items[].flags.spell-class-filter-for-5e.parentClass',
    'items[].flags.custom-character-sheet-sections.sectionName',
];
const MODULE = 'A module flag with no meaning for the sheet (appendix A section 6)';
const JS = 'Holds JavaScript, never executed or rendered (appendix A section 6)';
/**
 * Kept only in the raw asset. The module-flag prefixes of appendix A section
 * 6, then `prototypeToken`, `system.bastion`, `_stats`, `ownership` and
 * `folder`. A prefix that starts at a document key (`flags`, `_stats`,
 * `ownership`, `folder`) applies to the actor and to each embedded item.
 */
export const FOUNDRY_IGNORED_PREFIXES = [
    { prefix: 'flags.midi-qol', reason: MODULE },
    { prefix: 'flags.midiProperties', reason: MODULE },
    {
        prefix: 'flags.dae',
        reason: `${MODULE}; dae.macro holds JavaScript, never executed or rendered`,
    },
    { prefix: 'flags.chris-premades', reason: MODULE },
    { prefix: 'flags.gambits-premades', reason: MODULE },
    { prefix: 'flags.itemacro', reason: JS },
    { prefix: 'flags.autoanimations', reason: MODULE },
    { prefix: 'flags.sequencer', reason: MODULE },
    { prefix: 'flags.walledtemplates', reason: MODULE },
    { prefix: 'flags.warpgate', reason: MODULE },
    { prefix: 'flags.tokenflip', reason: MODULE },
    { prefix: 'flags.LocknKey', reason: MODULE },
    { prefix: 'flags.infusions', reason: MODULE },
    { prefix: 'flags.rest-recovery', reason: MODULE },
    { prefix: 'flags.activity-macro', reason: MODULE },
    { prefix: 'flags.universalAnimations', reason: MODULE },
    { prefix: 'flags.crux', reason: MODULE },
    { prefix: 'flags.obsidian', reason: MODULE },
    { prefix: 'flags.region-attacher', reason: MODULE },
    { prefix: 'flags.enhanced-terrain-layer', reason: MODULE },
    { prefix: 'flags.boss-loot-assets-premium', reason: MODULE },
    { prefix: 'flags.link-item-resource-5e', reason: MODULE },
    { prefix: 'flags.scene-packer', reason: `${MODULE}; identity corroboration only` },
    { prefix: 'flags.core', reason: MODULE },
    {
        prefix: 'items[].flags.dnd5e.riders',
        reason: 'Item-level dnd5e bookkeeping (appendix A section 6)',
    },
    {
        prefix: 'items[].flags.dnd5e.last',
        reason: 'Item-level dnd5e bookkeeping (appendix A section 6)',
    },
    {
        prefix: 'flags.itemcollection.contentsData',
        reason: 'Stale copies of item documents; never inventory',
    },
    { prefix: 'flags.item-piles', reason: 'Stale nested copies of item documents; never inventory' },
    { prefix: 'flags.ddbimporter.dndbeyond.totalLevels', reason: 'Stale D&D Beyond importer data' },
    { prefix: 'flags.ddbimporter.acEffects', reason: 'Stale D&D Beyond importer data' },
    { prefix: 'flags.ddbimporter.baseAC', reason: 'Stale D&D Beyond importer data' },
    { prefix: 'flags.ddbimporter.overrideAC', reason: 'Stale D&D Beyond importer data' },
    {
        prefix: 'prototypeToken',
        reason: 'The token; its image is handled with the portrait (appendix A section 7)',
    },
    { prefix: 'system.bastion', reason: 'Bastions are a 2024 rule' },
    { prefix: '_stats', reason: 'Version stamps, read by layout detection only' },
    { prefix: 'ownership', reason: 'Foundry permissions' },
    { prefix: 'folder', reason: 'Foundry folder' },
];
const DOCUMENT_KEYS = ['flags', '_stats', 'ownership', 'folder'];
/** The root-anchored forms of an ignored prefix: the actor's, and each embedded item's for document keys. */
export function ignoredForms(prefix) {
    const first = prefix.split('.')[0] ?? '';
    return DOCUMENT_KEYS.includes(first) ? [prefix, `items[].${prefix}`] : [prefix];
}
export function isIgnored(path, prefixes = FOUNDRY_IGNORED_PREFIXES) {
    return prefixes.some(({ prefix }) => ignoredForms(prefix).some((form) => isPrefixOf(form, path)));
}
/** Whether a row covers a leaf: the row names it, or the leaf is an empty container the table knows. */
export function isCovered(leaf, table = FOUNDRY_COVERAGE) {
    return table.some((r) => patternMatches(r.path, leaf) || isPrefixOf(leaf, r.path));
}
export function isWhitelisted(leaf, prefixes = FOUNDRY_WHITELISTED_PREFIXES) {
    return prefixes.some((p) => isPrefixOf(p, leaf));
}
/**
 * The leaf paths of an actor export under the whitelisted prefixes that
 * neither a coverage row nor an ignored prefix accounts for. Paths only,
 * never values.
 */
export function uncoveredPaths(actor, table = FOUNDRY_COVERAGE) {
    return leafPaths(actor).filter((leaf) => isWhitelisted(leaf) && !isIgnored(leaf) && !isCovered(leaf, table));
}
/** Pairs of an ignored prefix and a coverage row it would swallow; empty when the two lists agree. */
export function ignoredConflicts(table = FOUNDRY_COVERAGE, prefixes = FOUNDRY_IGNORED_PREFIXES) {
    const out = [];
    for (const { prefix } of prefixes) {
        for (const form of ignoredForms(prefix)) {
            for (const r of table)
                if (isPrefixOf(form, r.path))
                    out.push({ prefix, path: r.path });
        }
    }
    return out;
}
//# sourceMappingURL=foundry.js.map